import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import InputMask from 'react-input-mask';
import Select from 'react-select';
import SquareLoader from 'components/atoms/square_loader';
import { UsersTypes } from 'store/modules/my_account_store/types';
import {
  createUser,
  getUserFormOptions,
  updateUser,
  UserActionResponse,
  UserFormOption,
  UserFormPayload,
  UserInfoResponse,
} from 'services/actions/users_actions';
import { isSupportChatOnline, openSupportChat } from 'services/support_chat';
import { Container } from './styles';

export type UserFormMode = 'create' | 'edit';

interface UserFormModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  mode: UserFormMode;
  user?: UsersTypes | null;
  initialInfo?: UserInfoResponse | null;
  onSuccess: (response: UserActionResponse) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_NAME_PARTS = 2;
const MIN_NAME_PART_LENGTH = 2;

const isFullNameValid = (value: string): boolean => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length < MIN_NAME_PARTS) return false;
  return parts.every((part) => part.length >= MIN_NAME_PART_LENGTH);
};

const joinPhone = (ddd?: string, numero?: string): string =>
  ddd || numero ? `${ddd || ''}${numero || ''}` : '';

// Vazio é válido (limpa o número); preenchido precisa ter DDD + número
// completos, senão o submit apagaria o telefone já cadastrado.
const isPhoneIncomplete = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length > 0 && digits.length < 10;
};

const UserFormModal: React.FC<UserFormModalProps> = ({
  showModal,
  setShowModal,
  mode,
  user,
  initialInfo,
  onSuccess,
}) => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [celular, setCelular] = useState<string>('');
  const [celularAdicional, setCelularAdicional] = useState<string>('');
  const [telefone, setTelefone] = useState<string>('');
  const [ramal, setRamal] = useState<string>('');
  const [roleId, setRoleId] = useState<string>('');
  const [genreId, setGenreId] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Duplicidade detectada + atendente online: em vez de abrir protocolo,
  // oferecemos o chat ao vivo (chatContext vai como mensagem de abertura).
  const [chatSuggested, setChatSuggested] = useState<boolean>(false);
  const [chatContext, setChatContext] = useState<string>('');

  const [roles, setRoles] = useState<UserFormOption[]>([]);
  const [genres, setGenres] = useState<UserFormOption[]>([]);
  const [availableFilters, setAvailableFilters] = useState<UserFormOption[]>([]);
  const [selectedFilterIds, setSelectedFilterIds] = useState<number[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState<boolean>(false);

  // Qualquer usuário do cliente edita todos os campos (admin não é mais exigido).
  const showFullFields = true;

  useEffect(() => {
    if (!showModal) return;

    setName(user?.nome || '');
    setEmail(user?.email || '');
    setErrorMessage('');
    setSelectedFilterIds([]);
    setChatSuggested(false);
    setChatContext('');

    const contact = mode === 'edit' ? initialInfo?.user?.contact_client : null;
    setCelular(joinPhone(contact?.ddd_celular, contact?.numero_celular));
    setCelularAdicional(
      joinPhone(contact?.ddd_celular_adicional1, contact?.numero_celular_adicional1),
    );
    setTelefone(joinPhone(contact?.ddd_telefone, contact?.numero_telefone));
    setRamal(contact?.ramal_telefone || '');
    setRoleId(contact?.role?.id ? String(contact.role.id) : '');
    setGenreId(contact?.genre?.id ? String(contact.genre.id) : '');
  }, [showModal, user, initialInfo, mode]);

  useEffect(() => {
    if (!showModal || optionsLoaded) return;

    getUserFormOptions()
      .then((response) => {
        setRoles(response?.data?.roles || []);
        setGenres(response?.data?.genres || []);
        setAvailableFilters(response?.data?.filters || []);
        setOptionsLoaded(true);
      })
      .catch(() => {
        setErrorMessage('Não foi possível carregar as opções do formulário.');
      });
  }, [showModal, optionsLoaded]);

  const title = mode === 'create' ? 'Adicionar usuário' : 'Editar usuário';

  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: String(role.id), label: role.nome })),
    [roles],
  );
  const selectedRole = useMemo(
    () => roleOptions.find((option) => option.value === roleId) || null,
    [roleOptions, roleId],
  );

  const nameValid = useMemo(() => isFullNameValid(name), [name]);
  const showNameHint = name.trim().length > 0 && !nameValid;

  const celularIncomplete = isPhoneIncomplete(celular);
  const celularAdicionalIncomplete = isPhoneIncomplete(celularAdicional);
  const telefoneIncomplete = isPhoneIncomplete(telefone);

  const celularDigits = celular.replace(/\D/g, '');
  const celularAdicionalDigits = celularAdicional.replace(/\D/g, '');
  const celularAdicionalRepetido =
    celularAdicionalDigits.length >= 10 && celularAdicionalDigits === celularDigits;

  const toggleFilter = (filterId: number) => {
    setSelectedFilterIds((prev) =>
      prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId],
    );
  };

  const allFiltersSelected =
    availableFilters.length > 0 && selectedFilterIds.length === availableFilters.length;

  const isFormValid = useMemo(() => {
    if (!nameValid) return false;
    if (showFullFields && !EMAIL_REGEX.test(email.trim())) return false;
    if (showFullFields && (celularIncomplete || celularAdicionalIncomplete || telefoneIncomplete))
      return false;
    if (showFullFields && celularAdicionalRepetido) return false;
    // Todo usuário novo nasce vinculado a pelo menos um filtro.
    if (mode === 'create' && selectedFilterIds.length === 0) return false;
    return true;
  }, [
    nameValid,
    email,
    showFullFields,
    celularIncomplete,
    celularAdicionalIncomplete,
    telefoneIncomplete,
    celularAdicionalRepetido,
    mode,
    selectedFilterIds,
  ]);

  const handleSubmit = async () => {
    if (!isFormValid || submitting) return;

    setSubmitting(true);
    setErrorMessage('');

    const payload: UserFormPayload = { nome: name.trim() };

    if (showFullFields) {
      payload.email = email.trim().toLowerCase();

      // Números incompletos: no modo edição envia vazio explicitamente, o que
      // permite limpar um número já cadastrado.
      const applyPhone = (
        dddKey: 'ddd_celular' | 'ddd_celular_adicional1' | 'ddd_telefone',
        numeroKey: 'numero_celular' | 'numero_celular_adicional1' | 'numero_telefone',
        value: string,
      ) => {
        const onlyDigits = value.replace(/\D/g, '');
        if (onlyDigits.length >= 10) {
          payload[dddKey] = onlyDigits.slice(0, 2);
          payload[numeroKey] = onlyDigits.slice(2);
        } else if (mode === 'edit') {
          payload[dddKey] = '';
          payload[numeroKey] = '';
        }
      };

      applyPhone('ddd_celular', 'numero_celular', celular);
      applyPhone('ddd_celular_adicional1', 'numero_celular_adicional1', celularAdicional);
      applyPhone('ddd_telefone', 'numero_telefone', telefone);

      const telefoneDigits = telefone.replace(/\D/g, '');
      if (telefoneDigits.length >= 10) {
        payload.ramal_telefone = ramal.trim();
      } else if (mode === 'edit') {
        // Ramal sem telefone fixo não faz sentido: limpa junto.
        payload.ramal_telefone = '';
      }
    }

    if (roleId) payload.cargo_id = Number(roleId);
    if (genreId) payload.sexo_id = Number(genreId);
    if (mode === 'create') payload.filter_ids = selectedFilterIds;

    try {
      const response =
        mode === 'create' ? await createUser(payload) : await updateUser(user!.id, payload);

      const data = response?.data;

      if (data?.success && data.action === 'chat_suggested') {
        // Solicitação precisa de atendimento humano: oferece a conversa ao vivo.
        // No CNL Chat o próprio widget trata o fora de horário e apresenta os
        // formulários devidos. O reenvio com fallback_ticket abaixo só acontece
        // no Tawk, que depende do hospedeiro saber que não há atendente.
        if (isSupportChatOnline()) {
          setChatContext(data.data?.chat_context || '');
          setChatSuggested(true);
        } else {
          const fallbackPayload: UserFormPayload = { ...payload, fallback_ticket: true };
          const fallbackResponse =
            mode === 'create'
              ? await createUser(fallbackPayload)
              : await updateUser(user!.id, fallbackPayload);
          const fallbackData = fallbackResponse?.data;

          if (fallbackData?.success) {
            onSuccess(fallbackData);
            setShowModal(false);
          } else {
            setErrorMessage(fallbackData?.message || 'Não foi possível concluir a operação.');
          }
        }
      } else if (data?.success) {
        onSuccess(data);
        setShowModal(false);
      } else {
        setErrorMessage(data?.message || 'Não foi possível concluir a operação.');
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      setErrorMessage(apiMessage || 'Erro ao se comunicar com o servidor. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChat = () => {
    // O contexto da solicitação só é enviado no Tawk: o CNL Chat ainda não
    // expõe API para abrir já com uma mensagem (ver services/support_chat).
    openSupportChat(chatContext);
    setShowModal(false);
  };

  return (
    <Modal className="m-0" size="lg" show={showModal} onHide={() => setShowModal(false)}>
      <Container>
        <div tabIndex={-1} role="dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="close"
                aria-label="Fechar"
                onClick={() => setShowModal(false)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="m-0 d-block" htmlFor="user-form-name">
                    Nome
                    <input
                      type="text"
                      className="form-control"
                      id="user-form-name"
                      placeholder="Digite nome e sobrenome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  {showNameHint && (
                    <p className="feedback-error mb-0 mt-1">Informe nome e sobrenome.</p>
                  )}
                </div>
              </div>

              {showFullFields && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="m-0 d-block" htmlFor="user-form-email">
                        E-mail
                        <input
                          type="email"
                          className="form-control"
                          id="user-form-email"
                          placeholder="Digite o e-mail"
                          maxLength={60}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="col-md-6">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="m-0 d-block" htmlFor="user-form-celular">
                        Celular
                        <InputMask
                          type="text"
                          className="form-control"
                          id="user-form-celular"
                          mask="(99) 99999-9999"
                          maskChar=""
                          placeholder="Digite o celular com DDD"
                          value={celular}
                          onChange={(e) => setCelular(e.target.value)}
                        />
                      </label>
                      {celularIncomplete && (
                        <p className="feedback-error mb-0 mt-1">
                          Celular incompleto. Informe o DDD e o número.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="m-0 d-block" htmlFor="user-form-celular-adicional">
                        Celular adicional
                        <InputMask
                          type="text"
                          className="form-control"
                          id="user-form-celular-adicional"
                          mask="(99) 99999-9999"
                          maskChar=""
                          placeholder="Opcional"
                          value={celularAdicional}
                          onChange={(e) => setCelularAdicional(e.target.value)}
                        />
                      </label>
                      {celularAdicionalIncomplete && (
                        <p className="feedback-error mb-0 mt-1">
                          Celular incompleto. Informe o DDD e o número.
                        </p>
                      )}
                      {celularAdicionalRepetido && (
                        <p className="feedback-error mb-0 mt-1">
                          O celular adicional deve ser diferente do celular principal.
                        </p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-7">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label className="m-0 d-block" htmlFor="user-form-telefone">
                            Telefone
                            <InputMask
                              type="text"
                              className="form-control"
                              id="user-form-telefone"
                              mask="(99) 9999-9999"
                              maskChar=""
                              placeholder="Fixo com DDD"
                              value={telefone}
                              onChange={(e) => setTelefone(e.target.value)}
                            />
                          </label>
                          {telefoneIncomplete && (
                            <p className="feedback-error mb-0 mt-1">
                              Telefone incompleto. Informe o DDD e o número.
                            </p>
                          )}
                        </div>
                        <div className="col-5">
                          <label className="m-0 d-block" htmlFor="user-form-ramal">
                            Ramal
                            <input
                              type="text"
                              className="form-control"
                              id="user-form-ramal"
                              placeholder="Opcional"
                              maxLength={6}
                              value={ramal}
                              onChange={(e) => setRamal(e.target.value.replace(/\D/g, ''))}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="row mb-3">
                <div className="col-md-6">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="m-0 d-block" htmlFor="user-form-role">
                    Cargo
                    <Select
                      inputId="user-form-role"
                      classNamePrefix="user-form-role-select"
                      options={roleOptions}
                      value={selectedRole}
                      onChange={(option) => setRoleId(option ? option.value : '')}
                      placeholder="Busque o cargo"
                      noOptionsMessage={() => 'Nenhum cargo encontrado'}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      // O global.ts força .modal/.modal-backdrop com z-index 3000000000,
                      // então o menu portalado precisa ficar acima disso.
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000000001 }) }}
                    />
                  </label>
                </div>
                <div className="col-md-6">
                  <label className="m-0 d-block" htmlFor="user-form-genre">
                    Gênero
                    <select
                      className="form-control"
                      id="user-form-genre"
                      value={genreId}
                      onChange={(e) => setGenreId(e.target.value)}
                    >
                      <option value="" disabled>
                        Selecione o gênero
                      </option>
                      {genres.map((genre) => (
                        <option key={genre.id} value={genre.id}>
                          {genre.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {mode === 'create' && (
                <div className="row mb-3">
                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="font-weight-bold">Filtros</span>
                      {availableFilters.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() =>
                            setSelectedFilterIds(
                              allFiltersSelected ? [] : availableFilters.map((f) => f.id),
                            )
                          }
                        >
                          {allFiltersSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                        </button>
                      )}
                    </div>
                    <small className="text-muted d-block mb-2">
                      Marque ao menos um filtro que o usuário poderá visualizar.
                    </small>
                    {availableFilters.length === 0 ? (
                      <p className="text-muted mb-0">Nenhum filtro cadastrado para o cliente.</p>
                    ) : (
                      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {availableFilters.map((filter) => (
                          <div className="custom-control custom-checkbox" key={filter.id}>
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id={`user-form-filter-${filter.id}`}
                              checked={selectedFilterIds.includes(filter.id)}
                              onChange={() => toggleFilter(filter.id)}
                            />
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label
                              className="custom-control-label"
                              htmlFor={`user-form-filter-${filter.id}`}
                            >
                              {filter.nome}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {chatSuggested && (
                <div className="alert alert-info mb-0">
                  <strong>Fale com um consultor para concluir sua solicitação.</strong>
                </div>
              )}

              {errorMessage && <p className="feedback-error mb-0">{errorMessage}</p>}
            </div>

            <div className="modal-footer">
              {chatSuggested ? (
                <button type="button" className="btn btn-primary" onClick={handleOpenChat}>
                  Falar com um consultor
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={!isFormValid || submitting}
                >
                  Salvar
                </button>
              )}
              {submitting && <SquareLoader customClass="delete-word" />}
            </div>
          </div>
        </div>
      </Container>
    </Modal>
  );
};

UserFormModal.defaultProps = {
  user: null,
  initialInfo: null,
};

export default UserFormModal;
